import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vacancyService } from '../../services/vacancyService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import { STORAGE_BASE_URL } from '../../config/api';
import CompanyNav from '../../components/CompanyNav';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import Loader from '../../components/Loader';
import Swal from 'sweetalert2';
import { useTranslation } from '../../hooks/useTranslation';

const VacancyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [vacancy, setVacancy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('description'); // 'description' or 'applications'
    const [applicationsCount, setApplicationsCount] = useState(0);

    useEffect(() => {
        loadVacancy();
        loadApplicationsCount();
    }, [id]);

    const handleBack = () => {
        navigate('/company/vacancies');
    };

    const loadVacancy = async () => {
        try {
            setLoading(true);
            const response = await vacancyService.getCompanyVacancyById(id);

            if (response && response.success) {
                setVacancy(response.data);
            } else if (response?.data) {
                setVacancy(response.data);
            } else {
                toast.error(t('company.vacancyDetail.loadFailed') || 'Failed to load vacancy details');
                navigate('/company/vacancies');
            }
        } catch (error) {
            console.error('Error loading vacancy:', error);
            const errorMessage = error.response?.data?.message || t('company.vacancyDetail.loadFailed') || 'Failed to load vacancy details';
            toast.error(errorMessage);
            navigate('/company/vacancies');
        } finally {
            setLoading(false);
        }
    };

    const loadApplicationsCount = async () => {
        try {
            const response = await vacancyService.getVacancyApplications(id);
            if (response && response.success) {
                setApplicationsCount(response.data?.length || 0);
            } else if (Array.isArray(response)) {
                setApplicationsCount(response.length);
            } else if (response?.data) {
                setApplicationsCount(Array.isArray(response.data) ? response.data.length : 0);
            }
        } catch (error) {
            console.error('Error loading applications count:', error);
        }
    };

    const handleDelete = async () => {
        const dir = locale === 'ar' ? 'rtl' : 'ltr';
        const result = await Swal.fire({
            html: `<div style="text-align: center; direction: ${dir};">
                <p>${t('company.vacancyManagement.deleteConfirm')}</p>
                <p style="color: #dc2626; font-size: 0.85rem; margin-top: 0.5rem;">${t('company.vacancyManagement.deleteWarning')}</p>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg> ${t('company.vacancyManagement.delete')}</span>`,
            cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('company.settings.cancel')}</span>`,
            reverseButtons: locale === 'ar',
            customClass: {
                popup: 'swal2-rtl',
                title: 'swal2-title-rtl',
                htmlContainer: 'swal2-html-container-rtl',
                confirmButton: 'swal2-confirm-delete-rtl',
                cancelButton: 'swal2-cancel-rtl'
            }
        });

        if (result.isConfirmed) {
            try {
                const response = await vacancyService.delete(id);
                if (response.success) {
                    toast.success('تم حذف الوظيفة بنجاح');
                    navigate('/company/vacancies');
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'فشل حذف الوظيفة';
                toast.error(errorMessage);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('company.vacancyManagement.notSpecified');
        const date = new Date(dateString);
        if (locale === 'ar') {
            const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
            return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
        } else {
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
        }
    };

    const formatLocation = (location) => {
        if (!location) return t('employee.vacancyDetail.defaultLocation');
        if (typeof location === 'string') return location;
        if (typeof location === 'object' && (location.lat || location.long)) {
            return t('employee.vacancyDetail.defaultLocation');
        }
        return t('employee.vacancyDetail.defaultLocation');
    };

    const formatWorkType = (type) => {
        if (type === 'full_time') return t('employee.home.workType.fullTime');
        if (type === 'part_time') return t('employee.home.workType.partTime');
        if (type === 'contract') return t('employee.home.workType.contract');
        if (type === 'freelance') return t('employee.home.workType.freelance');
        return type;
    };

    const formatPresenceType = (type) => {
        if (type === 'remote') return t('employee.home.presenceType.remote');
        if (type === 'hybrid') return t('employee.home.presenceType.hybrid');
        if (type === 'onsite') return t('employee.home.presenceType.onsite');
        return type;
    };

    const formatStatus = (status) => {
        if (status === 'open') return t('company.vacancyDetail.open');
        if (status === 'closed') return t('company.vacancyDetail.closed');
        return status;
    };

    const getStatusBadgeClass = (status) => {
        return status === 'open' ? 'status-badge-accepted' : 'status-badge-rejected';
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    if (!vacancy) {
        return (
            <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                <CompanyNav user={user} />
                <div className="text-center py-4 px-3">
                    <p>{t('company.vacancyDetail.notFound') || 'Vacancy not found'}</p>
                    <button onClick={handleBack} className="btn-view-vacancy mt-3">
                        {t('company.vacancyDetail.backToList') || 'Back to List'}
                    </button>
                </div>
                <CompanyBottomNavigation />
            </div>
        );
    }

    const targetCompany = vacancy.target_company || vacancy.company_details?.target_company || vacancy.company;
    const companyName = vacancy.target_company_name || targetCompany?.name || user?.company?.name || t('employee.home.companyName');
    const companyLogo = targetCompany?.logo || targetCompany?.image || user?.company?.logo;

    return (
        <div className="vacancy-detail-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <CompanyNav user={user} />

            {/* Header Section */}
            <div className="detail-header px-3 py-3">
                <div className="d-flex justify-content-between align-items-center">
                    <button className="back-btn" onClick={handleBack}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d={locale === 'ar' ? "M5 12h14M12 5l7 7-7 7" : "M19 12H5M12 19l-7-7 7-7"}/>
                        </svg>
                    </button>
                    <h3 className="header-title mb-0">{vacancy.job_title?.name || t('company.vacancyDetail.title')}</h3>
                    <div className="d-flex gap-2">
                        <Link
                            to={`/company/vacancies/${id}/edit`}
                            className="btn-view-vacancy"
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            {t('company.vacancyDetail.edit')}
                        </Link>
                        <button
                            onClick={handleDelete}
                            className="btn-withdraw"
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem'
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                            {t('company.vacancyDetail.delete')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Job Header Section */}
            <div className="job-header-section px-3 mb-3">
                <div className="d-flex align-items-start gap-3 mb-3">
                    <div className="flex-grow-1">
                        <h1 className="job-title-detail mb-2">
                            {vacancy.job_title?.name || 'اسم الوظيفة'}
                        </h1>
                        <div className="job-tags-detail d-flex gap-2 mb-2">
                            <span className={`status-badge ${getStatusBadgeClass(vacancy.status)}`}>
                                {formatStatus(vacancy.status)}
                            </span>
                            <span className="job-tag-detail">{formatLocation(vacancy.location)} - مصر</span>
                            <span className="job-tag-detail">{formatDate(vacancy.created_at)}</span>
                        </div>
                    </div>
                    <div className="company-logo-detail">
                        {companyLogo ? (
                            <img
                                src={companyLogo.startsWith('http') ? companyLogo : `${STORAGE_BASE_URL}/${companyLogo}`}
                                alt={companyName}
                                className="logo-img-detail"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div className="company-logo-text-detail" style={{ display: companyLogo ? 'none' : 'flex' }}>
                            <b>{companyName.charAt(0).toUpperCase()}</b>
                        </div>
                    </div>
                </div>

                {/* Location Map */}
                {vacancy.has_location && vacancy.location_lat && vacancy.location_long && (
                    <div className="location-map mb-3 mt-3">
                        <a
                            href={vacancy.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${vacancy.location_lat},${vacancy.location_long}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="map-link"
                        >
                            <div className="map-placeholder">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                <span>عرض على الخريطة</span>
                            </div>
                        </a>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="detail-tabs-container px-3">
                <div className="detail-tabs d-flex gap-2">
                    <button
                        className={`detail-tab ${activeTab === 'description' ? 'active' : ''}`}
                        onClick={() => setActiveTab('description')}
                    >
                        وصف الوظيفة
                    </button>
                    <button
                        className={`detail-tab ${activeTab === 'applications' ? 'active' : ''}`}
                        onClick={() => setActiveTab('applications')}
                    >
                        الطلبات ({applicationsCount})
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="detail-content px-3 mb-5">
                {activeTab === 'applications' ? (
                    <div className="applications-tab-content">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className="section-title-detail mb-0">طلبات الوظيفة</h3>
                            <Link
                                to={`/company/vacancies/${id}/applications`}
                                className="btn-view-vacancy"
                            >
                                عرض جميع الطلبات
                            </Link>
                        </div>
                        {applicationsCount === 0 ? (
                            <p className="empty-state-text">لا توجد طلبات حتى الآن</p>
                        ) : (
                            <p className="section-text">يوجد {applicationsCount} طلب على هذه الوظيفة</p>
                        )}
                    </div>
                ) : (
                    <div className="description-tab-content">
                        {/* Job Information */}
                        <section className="content-section mb-4">
                            <h3 className="section-title-detail mb-3">{t('company.vacancyDetail.jobInformation') || 'معلومات عن الوظيفة'}</h3>
                            <ul className="detail-list">
                                <li>{t('company.vacancyDetail.jobTitle') || 'الوظيفة'}: {vacancy.job_title?.name || t('company.vacancyManagement.notSpecified')}</li>
                                <li>{t('company.vacancyDetail.academicDegree') || 'المؤهل'}: {vacancy.academic_degree || t('company.vacancyManagement.notSpecified')}</li>
                                <li>{t('company.vacancyDetail.experience') || 'الخبرات'}: {vacancy.experience ? `${vacancy.experience} ${t('employee.vacancyDetail.years') || 'سنوات'}` : t('company.vacancyManagement.notSpecified')}</li>
                                <li>{t('company.vacancyDetail.workType') || 'طبيعة العمل'}: {formatWorkType(vacancy.type_of_work)}</li>
                                <li>{t('company.vacancyDetail.presenceType') || 'نوع الحضور'}: {formatPresenceType(vacancy.presence_type)}</li>
                                {vacancy.salary && (
                                    <li>{t('company.vacancyDetail.salary') || 'الراتب'}: {vacancy.salary} {vacancy.currency || ''}</li>
                                )}
                            </ul>
                        </section>

                        {/* Skills Section */}
                        {vacancy.skills && vacancy.skills.length > 0 && (
                            <section className="content-section mb-4">
                                <h3 className="section-title-detail mb-3">{t('company.vacancyDetail.skills') || 'المهارات المطلوبة'}</h3>
                                <div className="d-flex flex-wrap gap-2">
                                    {vacancy.skills.map((skill) => (
                                        <span
                                            key={skill.id}
                                            className="status-badge status-badge-accepted"
                                            style={{
                                                padding: '0.5rem 1rem',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.9rem',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {skill.name}
                                        </span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Description */}
                        {vacancy.description && (
                            <section className="content-section mb-4">
                                <h3 className="section-title-detail mb-3">الوصف</h3>
                                <p className="section-text">
                                    {vacancy.description}
                                </p>
                            </section>
                        )}

                        {/* Requirements */}
                        {vacancy.description && vacancy.description.includes('\n') && (
                            <section className="content-section mb-4">
                                <h3 className="section-title-detail mb-3">المتطلبات</h3>
                                <ul className="detail-list">
                                    {vacancy.description.split('\n').filter(line => line.trim()).map((req, index) => (
                                        <li key={index}>{req.trim()}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Benefits and Facilities */}
                        {vacancy.privileges && (
                            <section className="content-section mb-4">
                                <h3 className="section-title-detail mb-3">المرافق والمميزات</h3>
                                {vacancy.privileges.includes('\n') ? (
                                    <ul className="detail-list">
                                        {vacancy.privileges.split('\n').filter(line => line.trim()).map((benefit, index) => (
                                            <li key={index}>{benefit.trim()}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="section-text">{vacancy.privileges}</p>
                                )}
                            </section>
                        )}
                    </div>
                )}
            </div>

            {/* View Applications Button */}
            {applicationsCount > 0 && (
                <div className="apply-button-container">
                    <Link
                        to={`/company/vacancies/${id}/applications`}
                        className="btn-apply-now"
                        style={{ textDecoration: 'none', display: 'block' }}
                    >
                        عرض جميع الطلبات ({applicationsCount})
                    </Link>
                </div>
            )}

            <CompanyBottomNavigation />
        </div>
    );
};

export default VacancyDetail;

